import React, {useCallback, useReducer, useState} from 'react';
import {StudentProfile} from 'grpc-bob/student_pb';
import {Plan} from 'grpc-bob/subscription_pb';
import {RouteComponentProps} from 'react-router';

import {Dialog, DialogContent, DialogTitle} from '@material-ui/core';
import AccountLayout from '../../layouts/AccountLayout';
import SimpleAlert from '../../components/SimpleAlert';
import SignInForm from '../../components/SignInForm';
import SignOutForm from '../../components/SignOutForm';
import PlanPicker from '../../components/PlanPicker';

import {useTranslation} from 'react-i18next';
import {useDispatchCtx} from '../../context';
import {IAuthenticator} from '../../hooks/useFirebaseAuth';

import {setMessage} from '../../state-management/app';
import reducer, {
  initialState, Phone,
  setLoading,
  setStep,
  setVerifyCode
} from '../../state-management/auth';

import {ErrorHandler, firebase} from '../../externals';
import {getFirebaseErrorMsg, parseQueryToField} from '../../utils';

import styles from './SignIn.module.css';
import 'react-intl-tel-input/dist/main.css';


interface SignInProps extends RouteComponentProps {
  user: StudentProfile.AsObject | undefined
  plans: Array<Plan.AsObject>
  firebaseAuthenticator: IAuthenticator
}


const SignIn = ({user, plans, history, location, firebaseAuthenticator}: SignInProps) => {
  const [{phone, step, alert, isLoading}, dispatch] = useReducer(reducer, initialState);
  const [isDialogOpened, setIsDialogOpened] = useState(false);
  const {t} = useTranslation();
  const appDispatch = useDispatchCtx();


  async function renewRecaptcha() {
    window.recaptchaVerifier.clear();
    window.recaptchaWidgetId = await window.recaptchaVerifier.render();
  }


  const onVerifyCode = useCallback(async (code: string) => {
    dispatch(setLoading(true));

    try { // false loading to help user know we are processing the verification
      await window.confirmationResult.confirm(code);
    } catch (e) {
      ErrorHandler.report(e.message, {user: phone.number});
      appDispatch(setMessage('invalidConfirmationCode'));
    } finally {

      new Promise(async (resolve) => {
        setTimeout(() => {
          resolve(true)
        }, 400)
      }).then(() => dispatch(setLoading(false)));
    }
  }, [appDispatch, phone]);


  const onChangeConfirmation = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setVerifyCode(e.target.value));

    if (e.target.value.length === 6) {
      onVerifyCode(e.target.value);
      ErrorHandler.report(`User verify with code: ${e.target.value}`, {user: phone.number});
    }
  }, [onVerifyCode, phone]);


  const onLogOut = useCallback(async () => {
    await firebase.auth!().signOut();
    window.location.reload();
  }, []);


  const goToPackage = useCallback(() => {
    const {planId} = parseQueryToField(location.search, ['planId']);

    if (planId) {
      return history.push(`/package?planId=${planId || 'Basic'}`);
    }

    setIsDialogOpened(true);
  }, [history, location.search]);


  const onGoBackTelInput = useCallback(() => {
    ErrorHandler.report(`User go back to step 1`, {user: phone.number});
    dispatch(setStep(0))
  }, [phone]);


  const onSignInPhone = useCallback(async (phone: Phone) => {
    if (phone === null) return;

    if (!phone.valid) {
      appDispatch(setMessage('invalidPhoneNumber'));
      return;
    }
    const userPhoneNumber = `+${phone!.code}${phone!.number}`;

    try {
      dispatch(setLoading(true));

      window.confirmationResult = await firebase.auth!().signInWithPhoneNumber(userPhoneNumber, window.recaptchaVerifier);

      dispatch(setStep(1));
    } catch (e) {

      ErrorHandler.report(e.message, {user: userPhoneNumber});
      appDispatch(setMessage('invalidPhoneNumber'));
      await renewRecaptcha();

    } finally {
      dispatch(setLoading(false));
    }
  }, [appDispatch]);


  const onSignInFacebook = useCallback((e) => {
    e.preventDefault();
    const provider = new firebase.auth!.FacebookAuthProvider();
    provider.setCustomParameters({
      'display': 'popup'
    });

    firebase.auth!().signInWithPopup(provider).then(async function (result) {
      if (result.user) {
        ErrorHandler.report(`User sign in facebook success`, {user: result.user.email});
      } else {
        ErrorHandler.report(`User login success but cant receive user: ${JSON.stringify(result)}`);
      }
    }).catch(function (error) {

      const firebaseSpecificErrMsg = getFirebaseErrorMsg(error.code);

      if (firebaseSpecificErrMsg) {
        appDispatch(setMessage(firebaseSpecificErrMsg));
      }

      ErrorHandler.report(error.message);
    });
  }, [appDispatch]);


  const onChoosePlan = useCallback((planId: string) => {
    history.push(`/package?planId=${planId}`)
  }, [history]);

  const isSignInSocialSuccessButNotExistInOurSystem = firebaseAuthenticator.currentUser && !user;

  return (
    <AccountLayout
      title={t('pleaseSignIn')}
      miniHref=""
      miniText=""
      isContentCentered
    >
      <div className={styles.paper}>
        <div className={styles.form}>
          {alert && <SimpleAlert type={alert.type}>{alert.children}</SimpleAlert>}
          {
            user ?
              <SignOutForm
                userName={user.name}
                onLogOut={onLogOut}
                onGoNext={goToPackage}
              /> :
              <SignInForm
                key={step}
                step={step}
                phone={phone}
                dispatch={dispatch}
                isLoading={isLoading}
                onSignInPhone={onSignInPhone}
                onGoBackTelInput={onGoBackTelInput}
                onSignInFacebook={onSignInFacebook}
                onChangeConfirmCode={onChangeConfirmation}
              />
          }
          {
            isSignInSocialSuccessButNotExistInOurSystem &&
            <div className={styles.socialWarning}>
              <span>{t('socialSignOut')}.</span>
              {' '}
              <strong
                className={styles.socialSignOut}
                onClick={onLogOut}
              >
                {t('signOut')}?
              </strong>
            </div>
          }
        </div>
        <Dialog classes={{paper: styles.paperDialog}} open={isDialogOpened}>
          <DialogTitle className={styles.dialogTitle}>
            {t('pleaseChooseYourPlan')}
          </DialogTitle>
          <DialogContent>
            <PlanPicker
              className={styles.planPicker}
              plans={plans}
              onChoosePlan={onChoosePlan}
            />
          </DialogContent>
        </Dialog>
      </div>
    </AccountLayout>
  );
};

export default SignIn;
